import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceArea } from 'recharts';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getProfile, parseProfileForFormula } from '../services/profileService';
import { getMemberLabData } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import CustomAlert from '../Components/CustomAlert';
import { useCustomAlert } from '../hooks/useCustomAlert';
import SgotFormula from '../formula/Sgot';
import SgptFormula from '../formula/Sgpt';
import AlpFormula from '../formula/Alp';
import { STATUS_COLORS, getStatusHex, getWorstStatusColor } from '../utils/healthStatusColors';
import { fetchLabDataByNameFallbacks } from '../utils/labRecordUtils';
import { getNormalRange, compareWithNormalRange } from '../utils/normalRangeUtils';

const LiverContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: 400;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #999;
  font-weight: normal;
  margin-top: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#34A853'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LiverIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin: 4px 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusLabel = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ChartSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #34A853;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const mergeLabEntries = (primary = [], fallback = []) => {
  const merged = [];
  const seen = new Set();
  const getItemDate = (item) => String(item?.dateadd ?? item?.date ?? '').trim();

  [...(primary || []), ...(fallback || [])].forEach((item) => {
    if (!item) return;
    const dedupeKey = [
      item.id ?? '',
      item.dateadd ?? '',
      item.name ?? '',
      item.value ?? '',
      item.result ?? '',
      item.memberid ?? '',
      item.campaignid ?? '',
    ].join('|');

    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    merged.push(item);
  });

  return merged.sort((a, b) => new Date(getItemDate(b) || 0) - new Date(getItemDate(a) || 0));
};

const parseFlexibleDate = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  const raw = String(dateValue).trim();
  if (!raw) return null;

  const isoDate = new Date(raw);
  if (!Number.isNaN(isoDate.getTime())) {
    return isoDate;
  }

  const ymdMatch = raw.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
  if (ymdMatch) {
    const [, yearText, monthText, dayText] = ymdMatch;
    let year = Number(yearText);
    if (year > 2400) year -= 543;
    return new Date(year, Number(monthText) - 1, Number(dayText));
  }

  const dmyMatch = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
  if (dmyMatch) {
    const [, dayText, monthText, yearText] = dmyMatch;
    let year = Number(yearText);
    if (yearText.length === 2) {
      year = 1957 + year;
    } else if (year > 2400) {
      year -= 543;
    }
    return new Date(year, Number(monthText) - 1, Number(dayText));
  }

  return null;
};

const LiverPage = () => {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useCustomAlert();
  const [loading, setLoading] = useState(true);
  const [sgotData, setSgotData] = useState([]);
  const [sgptData, setSgptData] = useState([]);
  const [alpData, setAlpData] = useState([]);
  const [bilirubinData, setBilirubinData] = useState([]);
  const [albuminData, setAlbuminData] = useState([]);
  const [proteinData, setProteinData] = useState([]);
  const [directBilirubinData, setDirectBilirubinData] = useState([]);
  const [globulinData, setGlobulinData] = useState([]);
  const [latestDate, setLatestDate] = useState('');
  const [latestValues, setLatestValues] = useState({
    sgot: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    sgpt: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    alp: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    bilirubin: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    albumin: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    protein: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    globulin: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray }
  });
  const [profileData, setProfileData] = useState({ age: 0, gender: '' });
  const [allRecords, setAllRecords] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    sgot: '',
    sgpt: '',
    alp: '',
    bilirubin: '',
    directBilirubin: '',
    albumin: '',
    protein: '',
    globulin: ''
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      sgot: record.sgot || '',
      sgpt: record.sgpt || '',
      alp: record.alp || '',
      bilirubin: record.bilirubin || '',
      directBilirubin: record.directBilirubin || '',
      albumin: record.albumin || '',
      protein: record.protein || '',
      globulin: record.globulin || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {

      const updates = [];

      if (editForm.sgot && editForm.sgot !== editingRecord.sgot) {
        updates.push(updateLabData(editingRecord.rawDate, 'sgot', editForm.sgot, 'h'));
      }
      if (editForm.sgpt && editForm.sgpt !== editingRecord.sgpt) {
        updates.push(updateLabData(editingRecord.rawDate, 'sgpt', editForm.sgpt, 'h'));
      }
      if (editForm.alp && editForm.alp !== editingRecord.alp) {
        updates.push(updateLabData(editingRecord.rawDate, 'alp', editForm.alp, 'h'));
      }
      if (editForm.bilirubin && editForm.bilirubin !== editingRecord.bilirubin) {
        updates.push(updateLabData(editingRecord.rawDate, 'bilirubin', editForm.bilirubin, 'h'));
      }
      if (editForm.albumin && editForm.albumin !== editingRecord.albumin) {
        updates.push(updateLabData(editingRecord.rawDate, 'albumin', editForm.albumin, 'h'));
      }
      if (editForm.protein && editForm.protein !== editingRecord.protein) {
        updates.push(updateLabData(editingRecord.rawDate, 'protein', editForm.protein, 'h'));
      }

      await Promise.all(updates);

      showAlert('อัปเดตข้อมูลเรียบร้อยแล้ว');

      await loadLiverData();
      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      showAlert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const getLiverItemDate = (item) => String(item?.dateadd ?? item?.date ?? '').trim();
  const getLiverDateKey = (item) => getLiverItemDate(item).split('T')[0].split(' ')[0] || '';
  const getLiverFieldValue = (item) => {
    const candidates = [item?.value, item?.result, item?.data];
    for (const candidate of candidates) {
      const raw = String(candidate ?? '').trim();
      if (!raw) continue;
      if (/^-?\d+(?:\.\d+)?$/.test(raw)) return raw;
    }
    for (const candidate of candidates) {
      const raw = String(candidate ?? '').trim();
      if (raw) return raw;
    }
    return '';
  };
  const setLiverField = (record, field, item) => {
    const incoming = getLiverFieldValue(item);
    if (!incoming) return;
    const current = String(record[field] ?? '').trim();
    const currentIsNumeric = /^-?\d+(?:\.\d+)?$/.test(current);
    const incomingIsNumeric = /^-?\d+(?:\.\d+)?$/.test(incoming);
    if (!current || (incomingIsNumeric && !currentIsNumeric) || (incomingIsNumeric === currentIsNumeric)) {
      record[field] = incoming;
    }
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const date = parseFlexibleDate(dateString);
    if (!date) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = (date.getFullYear() + 543).toString();
    return `${day}/${month}/${year}`;
  };

  const calculateRisk = (sgot, sgpt, alp, bilirubin, albumin, protein) => {
    const sgotVal = parseFloat(sgot) || 0;
    const sgptVal = parseFloat(sgpt) || 0;
    const alpVal = parseFloat(alp) || 0;
    const bilirubinVal = parseFloat(bilirubin) || 0;
    const albuminVal = parseFloat(albumin) || 0;
    const proteinVal = parseFloat(protein) || 0;

    if (!sgot && !sgpt && !alp && !bilirubin && !albumin && !protein) return '-';

    const isSgotAbnormal = sgotVal > 0 && sgotVal > 40;
    const isSgptAbnormal = sgptVal > 0 && sgptVal > 41;
    const isAlpAbnormal = alpVal > 0 && (alpVal < 30 || alpVal > 120);
    const isBilirubinAbnormal = bilirubinVal > 0 && (bilirubinVal < 0.1 || bilirubinVal > 1.2);
    const isAlbuminAbnormal = albuminVal > 0 && (albuminVal < 3.5 || albuminVal > 5.5);
    const isProteinAbnormal = proteinVal > 0 && (proteinVal < 6.0 || proteinVal > 8.3);

    if (isSgotAbnormal || isSgptAbnormal || isAlpAbnormal || isBilirubinAbnormal || isAlbuminAbnormal || isProteinAbnormal) {
      return 'ผิดปกติ';
    }

    return 'ปกติ';
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '';

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const date = parseFlexibleDate(dateString);
    if (!date) return '';

    const thaiYear = date.getFullYear() + 543;
    const thaiMonth = thaiMonths[date.getMonth()];

    return `${date.getDate()} ${thaiMonth} ${thaiYear}`;
  };

  const loadLiverData = async () => {
    try {
      setLoading(true);

      const profileResponse = await getProfile();
      const profile = profileResponse?.data || profileResponse?.profile || {};
      const { age, gender } = parseProfileForFormula(profile);
      const memberLabResult = await getMemberLabData();
      const memberLabData = memberLabResult?.labData || {};

      setProfileData({ age, gender });

      const sgotArray = await fetchLabDataByNameFallbacks(getLabDataByName, [
        'sgot',
        'liverfunctiontestsgot',
        'liverfunctiontestsgo',
        'sgotast',
        'liverfunctiontestsgotast',
        'ast',
        'sgotastresult',
        'liverfunctiontestsgotastresult',
      ]);
      sgotArray.sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA; // descending: newest first
      });

      const sgptArray = await fetchLabDataByNameFallbacks(getLabDataByName, [
        'sgpt',
        'liverfunctiontestsgpt',
        'sgptresult',
        'alt',
        'sgptalt',
        'liverfunctiontestsgo',
        'liverfunctiontestsgotalt',
        'liverfunctiontestsgotaltresult',
      ]);
      sgptArray.sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const alpArray = await fetchLabDataByNameFallbacks(getLabDataByName, [
        'alp',
        'liverfunctiontestalk',
        'liverfunctiontestalkp',
        'alkalinephosphatase',
        'liverfunctiontestalkalinephosphatase',
        'liverfunctiontestalkalinephosphataseresult',
      ]);
      alpArray.sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const bilirubinArray = await fetchLabDataByNameFallbacks(getLabDataByName, [
        'bilirubin',
        'liverfunctiontestbil',
        'total_bilirubin',
        'totalbilirubin',
        'bilirubintotal',
        'liverfunctiontestbilirubintotal',
        'liverfunctiontestbilirubintotalresult',
      ]);
      bilirubinArray.sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const albuminArray = await fetchLabDataByNameFallbacks(getLabDataByName, [
        'albumin',
        'liverfunctiontestalb',
        'liverfunctiontestalbumin',
        'liverfunctiontestalbuminresult',
      ]);
      albuminArray.sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const proteinArray = await fetchLabDataByNameFallbacks(getLabDataByName, [
        'protein',
        'totalprotein',
        'totalproteinresult',
        'liverfunctiontesttota',
        'liverunctiontesttota',
        'liverunctiontesttotalprotein',
        'liverfunctiontesttotalproteinresult',
        'liverunctiontesttotalproteinresult',
      ]);
      proteinArray.sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const directBilirubinArray = await fetchLabDataByNameFallbacks(getLabDataByName, [
        'direct_bilirubin',
        'directbilirubin',
        'bilirubindirect',
        'liverfunctiontestbilirubindirect',
        'liverfunctiontestbilirubindirectresult',
      ]);
      directBilirubinArray.sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const globulinArray = await fetchLabDataByNameFallbacks(getLabDataByName, [
        'globulin',
        'liverfunctiontestglo',
        'liverfunctiontestglobulin',
        'liverfunctiontestglobulinresult',
      ]);
      globulinArray.sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const mergedSgotArray = mergeLabEntries(sgotArray, memberLabData?.sgot || []);
      const mergedSgptArray = mergeLabEntries(sgptArray, memberLabData?.sgpt || []);
      const mergedAlpArray = mergeLabEntries(alpArray, memberLabData?.alp || []);
      const mergedBilirubinArray = mergeLabEntries(bilirubinArray, memberLabData?.total_bilirubin || []);
      const mergedAlbuminArray = mergeLabEntries(albuminArray, memberLabData?.albumin || []);
      const mergedProteinArray = mergeLabEntries(proteinArray, memberLabData?.total_protein || []);
      const mergedDirectBilirubinArray = mergeLabEntries(directBilirubinArray, memberLabData?.direct_bilirubin || []);
      const mergedGlobulinArray = mergeLabEntries(globulinArray, memberLabData?.globulin || []);


      const recordsMap = new Map();

      mergedSgotArray.forEach(item => {
        const date = getLiverDateKey(item);
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
        }
        setLiverField(recordsMap.get(date), 'sgot', item);
        recordsMap.get(date).rawDate = getLiverItemDate(item);
      });

      mergedSgptArray.forEach(item => {
        const date = getLiverDateKey(item);
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
        }
        setLiverField(recordsMap.get(date), 'sgpt', item);
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = getLiverItemDate(item);
      });

      mergedAlpArray.forEach(item => {
        const date = getLiverDateKey(item);
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
        }
        setLiverField(recordsMap.get(date), 'alp', item);
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = getLiverItemDate(item);
      });

      mergedBilirubinArray.forEach(item => {
        const date = getLiverDateKey(item);
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
        }
        setLiverField(recordsMap.get(date), 'bilirubin', item);
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = getLiverItemDate(item);
      });

      mergedAlbuminArray.forEach(item => {
        const date = getLiverDateKey(item);
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
        }
        setLiverField(recordsMap.get(date), 'albumin', item);
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = getLiverItemDate(item);
      });

      mergedProteinArray.forEach(item => {
        const date = getLiverDateKey(item);
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
        }
        setLiverField(recordsMap.get(date), 'protein', item);
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = getLiverItemDate(item);
      });

      mergedDirectBilirubinArray.forEach(item => {
        const date = getLiverDateKey(item);
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
        }
        setLiverField(recordsMap.get(date), 'directBilirubin', item);
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = getLiverItemDate(item);
      });

      mergedGlobulinArray.forEach(item => {
        const date = getLiverDateKey(item);
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
        }
        setLiverField(recordsMap.get(date), 'globulin', item);
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = getLiverItemDate(item);
      });

      const combinedRecords = Array.from(recordsMap.values()).sort((a, b) =>
        new Date(b.date || b.rawDate || 0) - new Date(a.date || a.rawDate || 0)
      );

      setAllRecords(combinedRecords);

      const sortedCombinedRecords = [...combinedRecords].sort(
        (a, b) => new Date(a.rawDate) - new Date(b.rawDate)
      );

      const processedSgot = sortedCombinedRecords.map(record => ({
        date: record.date || getLiverDateKey({ dateadd: record.rawDate, value: record.sgot }),
        value: record.sgot != null ? parseFloat(record.sgot) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedSgpt = sortedCombinedRecords.map(record => ({
        date: record.date || getLiverDateKey({ dateadd: record.rawDate, value: record.sgpt }),
        value: record.sgpt != null ? parseFloat(record.sgpt) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedAlp = sortedCombinedRecords.map(record => ({
        date: record.date || getLiverDateKey({ dateadd: record.rawDate, value: record.alp }),
        value: record.alp != null ? parseFloat(record.alp) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedBilirubin = sortedCombinedRecords.map(record => ({
        date: record.date || getLiverDateKey({ dateadd: record.rawDate, value: record.bilirubin }),
        value: record.bilirubin != null ? parseFloat(record.bilirubin) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedAlbumin = sortedCombinedRecords.map(record => ({
        date: record.date || getLiverDateKey({ dateadd: record.rawDate, value: record.albumin }),
        value: record.albumin != null ? parseFloat(record.albumin) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedProtein = sortedCombinedRecords.map(record => ({
        date: record.date || getLiverDateKey({ dateadd: record.rawDate, value: record.protein }),
        value: record.protein != null ? parseFloat(record.protein) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedDirectBilirubin = sortedCombinedRecords.map(record => ({
        date: record.date || getLiverDateKey({ dateadd: record.rawDate, value: record.directBilirubin }),
        value: record.directBilirubin != null ? parseFloat(record.directBilirubin) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedGlobulin = sortedCombinedRecords.map(record => ({
        date: record.date || getLiverDateKey({ dateadd: record.rawDate, value: record.globulin }),
        value: record.globulin != null ? parseFloat(record.globulin) || 0 : null,
        rawDate: record.rawDate
      }));

      setSgotData(processedSgot);
      setSgptData(processedSgpt);
      setAlpData(processedAlp);
      setBilirubinData(processedBilirubin);
      setAlbuminData(processedAlbumin);
      setProteinData(processedProtein);
      setDirectBilirubinData(processedDirectBilirubin);
      setGlobulinData(processedGlobulin);

      let mostRecentDate = '';
      if (sgotArray.length > 0) mostRecentDate = sgotArray[sgotArray.length - 1].dateadd;
      if (sgptArray.length > 0 && sgptArray[sgptArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = sgptArray[sgptArray.length - 1].dateadd;
      }
      if (alpArray.length > 0 && alpArray[alpArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = alpArray[alpArray.length - 1].dateadd;
      }
      if (bilirubinArray.length > 0 && bilirubinArray[bilirubinArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = bilirubinArray[bilirubinArray.length - 1].dateadd;
      }
      if (albuminArray.length > 0 && albuminArray[albuminArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = albuminArray[albuminArray.length - 1].dateadd;
      }
      if (proteinArray.length > 0 && proteinArray[proteinArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = proteinArray[proteinArray.length - 1].dateadd;
      }
      if (directBilirubinArray.length > 0 && directBilirubinArray[directBilirubinArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = directBilirubinArray[directBilirubinArray.length - 1].dateadd;
      }
      if (globulinArray.length > 0 && globulinArray[globulinArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = globulinArray[globulinArray.length - 1].dateadd;
      }

      if (mostRecentDate) {
        setLatestDate(formatThaiDate(mostRecentDate));
      }

      const latestVisitRecord = combinedRecords[0];

      if (latestVisitRecord?.rawDate || latestVisitRecord?.date) {
        setLatestDate(formatThaiDate(latestVisitRecord.rawDate || latestVisitRecord.date));
      }

      if (latestVisitRecord) {
        // ใช้ [0] เพราะ array ถูก sort descending แล้ว (ใหม่→เก่า)
        const latestSgot = latestVisitRecord.sgot ? { value: latestVisitRecord.sgot } : null;
        const latestSgpt = latestVisitRecord.sgpt ? { value: latestVisitRecord.sgpt } : null;
        const latestAlp = latestVisitRecord.alp ? { value: latestVisitRecord.alp } : null;
        const latestBilirubin = latestVisitRecord.bilirubin ? { value: latestVisitRecord.bilirubin } : null;
        const latestAlbumin = latestVisitRecord.albumin ? { value: latestVisitRecord.albumin } : null;
        const latestProtein = latestVisitRecord.protein ? { value: latestVisitRecord.protein } : null;
        const latestDirectBilirubin = latestVisitRecord.directBilirubin ? { value: latestVisitRecord.directBilirubin } : null;
        const latestGlobulin = latestVisitRecord.globulin ? { value: latestVisitRecord.globulin } : null;


        const parseLatest = (latestItem) => {
          const raw = String(latestItem?.value ?? '').trim();
          if (!raw) return { hasData: false, raw: '', numeric: null };
          const numeric = Number(raw);
          return {
            hasData: true,
            raw,
            numeric: Number.isFinite(numeric) ? numeric : null
          };
        };

        const buildNumericField = (latestItem, unit, decimals, formulaResult, fallbackGreenTitle = 'ปกติ') => {
          const parsed = parseLatest(latestItem);
          if (!parsed.hasData) {
            return { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray };
          }

          if (parsed.numeric === null) {
            return { value: parsed.raw, status: parsed.raw, color: STATUS_COLORS.gray, rawValue: parsed.raw };
          }

          // ปิด logic สีตามค่าผลตรวจ (แดง/เขียว) — บังคับให้เป็นสีเทาทั้งหมดทุกหมวด
          return {
            value: `${parsed.numeric.toFixed(decimals)} ${unit}`.trim(),
            status: formulaResult?.title || fallbackGreenTitle,
            // color: formulaResult ? getStatusHex(formulaResult.color) : STATUS_COLORS.green,
            color: STATUS_COLORS.gray,
            rawValue: parsed.numeric
          };
        };

        const buildRangeField = (latestItem, unit, decimals, min, max) => {
          const parsed = parseLatest(latestItem);
          if (!parsed.hasData) {
            return { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray };
          }

          if (parsed.numeric === null) {
            return { value: parsed.raw, status: parsed.raw, color: STATUS_COLORS.gray, rawValue: parsed.raw };
          }

          const isAbnormal = parsed.numeric < min || parsed.numeric > max;
          // ปิด logic สีตามค่าผลตรวจ (แดง/เขียว) — บังคับให้เป็นสีเทาทั้งหมดทุกหมวด
          return {
            value: `${parsed.numeric.toFixed(decimals)} ${unit}`.trim(),
            status: isAbnormal ? 'ผิดปกติ' : 'ปกติ',
            // color: isAbnormal ? STATUS_COLORS.red : STATUS_COLORS.green,
            color: STATUS_COLORS.gray,
            rawValue: parsed.numeric
          };
        };

        const sgotValue = buildNumericField(latestSgot, 'U/L', 0, latestSgot ? await SgotFormula(Number(latestSgot?.value), age, gender) : null);
        const sgptValue = buildNumericField(latestSgpt, 'U/L', 0, latestSgpt ? await SgptFormula(Number(latestSgpt?.value), age, gender) : null);
        const alpValue = buildNumericField(latestAlp, 'U/L', 0, latestAlp ? await AlpFormula(Number(latestAlp?.value), age, gender) : null);
        const bilirubinValue = buildRangeField(latestBilirubin, 'mg/dL', 1, 0.1, 1.2);
        const albuminValue = buildRangeField(latestAlbumin, 'g/dL', 1, 3.5, 5.5);
        const proteinValue = buildRangeField(latestProtein, 'g/dL', 1, 6.0, 8.3);
        const directBilirubinRange = await getNormalRange('Direct bilirubin', gender, age);
        const globulinRange = await getNormalRange('Globulin', gender, age);
        const buildMediumRangeField = (latestItem, testName, normalRangeData) => {
          const raw = String(latestItem?.value ?? '').trim();
          if (!raw) return { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray };
          const comparison = compareWithNormalRange(raw, normalRangeData?.normal_range, testName);
          // ปิด logic สีตามค่าผลตรวจ (แดง/เขียว) — บังคับให้เป็นสีเทาทั้งหมดทุกหมวด
          return {
            value: `${raw} ${normalRangeData?.unit || ''}`.trim(),
            status: comparison.title || testName,
            // color: getStatusHex(comparison.color),
            color: STATUS_COLORS.gray,
            rawValue: raw,
          };
        };
        const directBilirubinValue = buildMediumRangeField(latestDirectBilirubin, 'Direct bilirubin', directBilirubinRange);
        const globulinValue = buildMediumRangeField(latestGlobulin, 'Globulin', globulinRange);

        setLatestValues({
          sgot: sgotValue,
          sgpt: sgptValue,
          alp: alpValue,
          bilirubin: bilirubinValue,
          directBilirubin: directBilirubinValue,
          albumin: albuminValue,
          protein: proteinValue,
          globulin: globulinValue
        });
      } else {
        setLatestDate('');
        setLatestValues({
          sgot: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
          sgpt: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
          alp: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
          bilirubin: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
          directBilirubin: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
          albumin: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
          protein: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
          globulin: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray }
        });
      }

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLiverData();
  }, []);

  return (
    <LiverContainer>
      <MainHeader />

      <TopBar>ตับ</TopBar>

      <Header>
        <BackButton onClick={handleBack}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <LiverIcon src="./ตับสี.png" alt="ตับ" style={{ width: '32px', height: '32px' }} />
          ตับ (Liver Function)
        </PageTitle>
        {latestDate && (
          <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>
        )}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : (
          <>
            {/* Status Card */}
            {/* ปิด logic การตัดสินสี/สถานะ (แดง/เขียว/ผิดปกติ) ทั้งหมด — เหลือแค่ไอคอน + ชื่อหมวดเท่านั้น */}
            {/* Status Card — comment ทั้งการ์ดออก เพราะย้ายไอคอนไปไว้ที่ Header ด้านบนแล้ว */}
            {/* {(() => {
              // const fields = [
              //   latestValues.sgot,
              //   latestValues.sgpt,
              //   latestValues.alp,
              //   latestValues.bilirubin,
              // ];
              // const hasData = fields.some((item) => item?.value && item.value !== '-');
              // const hasAbnormal = fields.some((item) => item?.color === STATUS_COLORS.red);
              // const hasKnownStatus = fields.some((item) => item?.color === STATUS_COLORS.green || item?.color === STATUS_COLORS.red);
              // const cardColor = getWorstStatusColor(fields.map((item) => item?.color));
              const cardColor = STATUS_COLORS.gray;

              return (
                <StatusCard $bgColor={cardColor}>
                  <LiverIcon src="./ตับสี.png" alt="ตับ" />
                  <StatusInfo>
                    <StatusTitle style={{ color: 'white' }}>ตับ</StatusTitle>
                    {!hasData ? (
                      <StatusTitle style={{ color: 'white' }}>ไม่มีข้อมูล</StatusTitle>
                    ) : hasKnownStatus && !hasAbnormal ? (
                      <StatusTitle style={{ color: 'white' }}>ผลตรวจปกติ</StatusTitle>
                    ) : !hasKnownStatus ? (
                      <StatusTitle style={{ color: 'white' }}>ไม่มีข้อมูลที่แปลผลได้</StatusTitle>
                    ) : (
                      <>
                        {latestValues.sgot?.color === STATUS_COLORS.red && (
                          <AbnormalItem>
                            <AbnormalName>SGOT (AST)</AbnormalName>
                            <AbnormalValue>{latestValues.sgot.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.sgot.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {latestValues.sgpt?.color === STATUS_COLORS.red && (
                          <AbnormalItem>
                            <AbnormalName>SGPT (ALT)</AbnormalName>
                            <AbnormalValue>{latestValues.sgpt.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.sgpt.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {latestValues.alp?.color === STATUS_COLORS.red && (
                          <AbnormalItem>
                            <AbnormalName>ALP</AbnormalName>
                            <AbnormalValue>{latestValues.alp.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.alp.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {latestValues.bilirubin?.color === STATUS_COLORS.red && (
                          <AbnormalItem>
                            <AbnormalName>Total Bilirubin</AbnormalName>
                            <AbnormalValue>{latestValues.bilirubin.value}</AbnormalValue>
                            <AbnormalStatus>ผิดปกติ</AbnormalStatus>
                          </AbnormalItem>
                        )}
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()} */}

            {/* SGOT Chart */}
            <ChartSection>
              <ChartTitle>SGOT (AST)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (≤ 40)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 40)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={sgotData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={40} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={40} y2={100} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF5722"
                    strokeWidth={2}
                    dot={{ fill: '#FF5722', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* SGPT Chart */}
            <ChartSection>
              <ChartTitle>SGPT (ALT)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (≤ 41)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 41)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={sgptData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={41} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={41} y2={100} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FFA726"
                    strokeWidth={2}
                    dot={{ fill: '#FFA726', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* ALP Chart */}
            <ChartSection>
              <ChartTitle>Alkaline Phosphatase (ALP)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 30)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (30-120)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 120)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={alpData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 200]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={30} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={30} y2={120} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={120} y2={200} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ fill: '#2196F3', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Total Bilirubin Chart */}
            <ChartSection>
              <ChartTitle>Total Bilirubin (บิลิรูบินรวม)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 0.1)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (0.1-1.2)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 1.2)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={bilirubinData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 2.5]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={0.1} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={0.1} y2={1.2} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={1.2} y2={2.5} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF9800"
                    strokeWidth={2}
                    dot={{ fill: '#FF9800', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Albumin Chart */}
            <ChartSection>
              <ChartTitle>Albumin (อัลบูมิน)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 3.5)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (3.5-5.5)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 5.5)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={albuminData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 7]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={3.5} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={3.5} y2={5.5} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={5.5} y2={7} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#9C27B0"
                    strokeWidth={2}
                    dot={{ fill: '#9C27B0', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Total Protein Chart */}
            <ChartSection>
              <ChartTitle>Total Protein (โปรตีนรวม)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 6.0)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (6.0-8.3)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 8.3)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={proteinData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={6.0} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={6.0} y2={8.3} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={8.3} y2={10} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#34A853"
                    strokeWidth={2}
                    dot={{ fill: '#34A853', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Direct Bilirubin Chart */}
            <ChartSection>
              <ChartTitle>Direct Bilirubin</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 0.3)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (0.3-0.8)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 0.8)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={directBilirubinData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 5]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={0.3} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={0.3} y2={0.8} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={0.8} y2={5} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF7043"
                    strokeWidth={2}
                    dot={{ fill: '#FF7043', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Globulin Chart */}
            <ChartSection>
              <ChartTitle>Globulin</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 2.0)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (2.0-3.5)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 3.5)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={globulinData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 6]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={2.0} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={2.0} y2={3.5} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={3.5} y2={6} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8E24AA"
                    strokeWidth={2}
                    dot={{ fill: '#8E24AA', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            <HistorySection>
              <HistoryTitle>ประวัติการตรวจ</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  ) : (
                    allRecords.map((record, index) => {
                      const values = [];
                      if (record.sgot) values.push(`SGOT: ${record.sgot}`);
                      if (record.sgpt) values.push(`SGPT: ${record.sgpt}`);
                      if (record.alp) values.push(`ALP: ${record.alp}`);
                      if (record.bilirubin) values.push(`Bilirubin: ${record.bilirubin}`);
                      if (record.directBilirubin) values.push(`Direct bilirubin: ${record.directBilirubin}`);
                      if (record.albumin) values.push(`Albumin: ${record.albumin}`);
                      if (record.protein) values.push(`Protein: ${record.protein}`);
                      if (record.globulin) values.push(`Globulin: ${record.globulin}`);

                      return (
                        <TableRow key={index}>
                          <TableCell>{formatDisplayDate(record.date)}</TableCell>
                          <TableCell>{values.join(', ') || '-'}</TableCell>
                          <TableCell>{calculateRisk(record.sgot, record.sgpt, record.alp, record.bilirubin, record.albumin, record.protein)}</TableCell>
                          <TableCell>
                            {/* ปิดการแก้ไขข้อมูลจากตารางประวัติ — comment ไอคอนแก้ไขไว้ ไม่ลบ */}
                            {/* <EditButton onClick={() => handleEditClick(record)}>
                              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </EditButton> */}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>SGOT (AST) (U/L)</Label>
              <Input
                type="text"
                step="1"
                value={editForm.sgot}
                onChange={(e) => setEditForm({ ...editForm, sgot: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>SGPT (ALT) (U/L)</Label>
              <Input
                type="text"
                step="1"
                value={editForm.sgpt}
                onChange={(e) => setEditForm({ ...editForm, sgpt: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Alkaline Phosphatase (U/L)</Label>
              <Input
                type="text"
                step="1"
                value={editForm.alp}
                onChange={(e) => setEditForm({ ...editForm, alp: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Total Bilirubin (mg/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.bilirubin}
                onChange={(e) => setEditForm({ ...editForm, bilirubin: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Direct bilirubin (mg/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.directBilirubin}
                onChange={(e) => setEditForm({ ...editForm, directBilirubin: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Albumin (g/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.albumin}
                onChange={(e) => setEditForm({ ...editForm, albumin: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Total Protein (g/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.protein}
                onChange={(e) => setEditForm({ ...editForm, protein: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Globulin (g/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.globulin}
                onChange={(e) => setEditForm({ ...editForm, globulin: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {alertState.isOpen && (
        <CustomAlert message={alertState.message} onClose={closeAlert} />
      )}
    </LiverContainer>
  );
};

export default LiverPage;
